import throttle from 'lodash/throttle';
import logger from 'utils/logger';

const audioContextOptions: AudioContextOptions = {
  latencyHint: 'playback',
  sampleRate: 8000
};

/**
 * Represents an object that can be used to monitor microphone volume.
 */
class AudioMonitor {
  /**
   * Initializes a new instance of the AudioMonitor class.
   * @param {Function} onUpdate The function to be called when there is an updated volume value
   * @param {Function} onSuspend The function to be called when AudioContext cannot be started
   */

  audioContext: any;
  analyser: any;
  onAudioProcess: Function;
  script: any;
  isConnected: boolean;
  stream: any; // Promise<MediaStream>;
  microphone: any;

  /*
    Placeholder for all stream created in AudioMonitor class. (https://lifesize.atlassian.net/browse/CA-2824)
    AudioButton component intermittently will create ghost stream which can't be reset while creating new stream.
    Ghost stream will hand around and keep microphone occupied, sometime it will go away and sometime it will live until browser is reloaded.
    newly created stream will be saved in this map and it will make sure that all streams are accounted for and
    will be cleared when call ends to make sure it releases microphone and user will not see red dot any more.
  * */
  private streamMap: Map<string, MediaStream> = new Map();

  constructor(onUpdate, onSuspend) {
    this.audioContext = new AudioContext(audioContextOptions);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.smoothingTimeConstant = 0.4;
    this.analyser.fftSize = 32;
    this.analyser.minDecibels = -80;
    this.analyser.maxDecibels = -10;
    this.onAudioProcess = throttle(() => this.processAudio(onUpdate), 100, { leading: true, trailing: false });
    // this.sampleDataArray = [];
    this.script = this.audioContext.createScriptProcessor(256, 1, 1);
    this.script.addEventListener('audioprocess', this.onAudioProcess);
    this.isConnected = false;
    if (this.audioContext.state === 'suspended') {
      onSuspend();
    }
  }

  processAudio(onUpdate) {
    const array = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(array);
    const max = (Math.max(...array) / 256).toFixed(2);
    onUpdate({ micLevel: max, sampleData: Array.from(array) });
  }

  /**
   * The connect method connets the audio monitor with a microphone
   * @param {string} deviceId The deviceId that identifies the source (microphone)
   */
  async connect(deviceId) {
    // prevent initialization of unnecessary stream when deviceId is missing.
    if (!deviceId) return;
    if (this.isConnected || this.stream?.active) {
      this.disconnect();
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: { deviceId }
      });
      // make sure that existing key does not get overwritten.
      this.streamMap.set(this.stream?.id || new Date().getTime(), this.stream);
      this.microphone = this.audioContext.createMediaStreamSource(this.stream);
      this.microphone.connect(this.analyser);
      this.analyser.connect(this.script);
      this.script.connect(this.audioContext.destination);
      if (this.audioContext?.state === 'suspended') {
        this.audioContext?.resume();
      }
      this.isConnected = true;
    } catch (e) {
      logger.warn(`AudioMonitor: Error trying to connect to deviceId ${deviceId}: ${e}`);
    }
  }

  stopTracks(stream: MediaStream) {
    stream?.getTracks().forEach((t) => {
      t.onended = null;
      t.stop();
      stream.removeTrack(t); // extra clean up for native clients
    });
  }

  /**
   * The disconnect method disconnects the audio monitor from the previously connected microphone
   */
  disconnect() {
    if (this.stream) {
      this.stopTracks(this.stream);
    }
    this.streamMap.delete(this.stream?.id);
    if (this.streamMap.size > 0) {
      this.streamMap.forEach((stream, key) => {
        this.stopTracks(stream);
        this.streamMap.delete(key);
      });
    }
    if (this.microphone) {
      this.microphone.disconnect();
    }
    this.analyser.disconnect();
    this.script.disconnect();
    this.isConnected = false;
  }

  /**
   * The destroy method disconnects and closes the audio monitor
   */
  async destroy() {
    this.script.removeEventListener('audioprocess', this.onAudioProcess);
    this.disconnect();
    await this.audioContext?.close();
  }
}

export default AudioMonitor;
